import { useApi } from '@/api';
import {
  ActivityCard,
  EntityName,
  ExternalLinkIcon,
  KpiCard,
  Link,
  LinkButton,
  Page
} from '@/components';
import { useSearchParams } from '@/hooks';
import { trendVariantInverted } from '@/utils';
import { farmMapping } from '@/utils/farms';
import {
  CryptoIcon,
  EtherScanPictogram,
  Flex,
  Heading,
  LinkWrapper
} from '@blockanalitica/ui';
import { useParams } from 'react-router-dom';
import DepositorsDonutChart from './DepositorsDonutChart';
import HistoricTabs from './HistoricTabs';
import TopDepositors from './TopDespositors';

export default function StakePage() {
  const { address } = useParams();
  const [daysAgo] = useSearchParams('days_ago', '1');

  const [data, error] = useApi({
    url: `/farms/${address}/`,
    queryParams: {
      days_ago: daysAgo
    }
  });

  if (error) {
    throw error;
  }
  if (data) {
    const currency =
      farmMapping(address) !== 'chronicle_farm' ? 'rewards' : 'points';

    let symbol = data.staking_token_symbol;
    let title =
      'Supply ' +
      symbol +
      ' to earn ' +
      data.rewards_token_symbol +
      ' ' +
      currency;
    let url = `https://app.sky.money/?widget=rewards&reward=${address}`;
    if (symbol === 'lsMKR') {
      symbol = 'MKR';
      title =
        'Seal ' +
        symbol +
        ' to earn ' +
        data.rewards_token_symbol +
        ' ' +
        currency;
      url = `https://app.sky.money/?widget=seal`;
    }
    if (symbol === 'lsSKY') {
      symbol = 'SKY';
      title =
        'Stake ' +
        symbol +
        ' to earn ' +
        data.rewards_token_symbol +
        ' ' +
        currency;
      url = `https://app.sky.money/?widget=stake`;
    }

    return (
      <Page
        headerFlexDirection={['column', 'row']}
        title={
          <EntityName
            symbol1={
              <CryptoIcon
                name={data.staking_token_symbol}
                address={data.staking_token_address}
                size="3"
              />
            }
            symbol2={
              <CryptoIcon
                name={data.rewards_token_symbol}
                address={data.rewards_token_address}
                size="3"
              />
            }>
            <Heading size="1">
              <Flex gap="xs" alignItems="center">
                {title}
                <a
                  href={`https://etherscan.io/address/${address}`}
                  target="_blank"
                  rel="noreferrer">
                  <EtherScanPictogram size="0" />
                </a>
              </Flex>
            </Heading>
          </EntityName>
        }
        actions={
          <LinkButton to={url} target="_blank">
            <Flex
              gap="3xs"
              alignItems="center"
              style={{ whiteSpace: 'nowrap' }}>
              {farmMapping(data.address) !== 'chronicle_farm' ? (
                <>
                  Start staking <ExternalLinkIcon size="1" />
                </>
              ) : (
                <>
                  Start getting points <ExternalLinkIcon size="1" />
                </>
              )}
            </Flex>
          </LinkButton>
        }>
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          {farmMapping(data.address) === 'chronicle_farm' ? (
            <>
              <KpiCard
                variant="usds"
                title="Total Staked"
                data={data}
                field="total_staked"
                options={{
                  currency: {
                    name: data.staking_token_symbol,
                    address: data.staking_token_address
                  }
                }}
              />
              <KpiCard
                title={`Total estimated points`}
                data={data}
                field="total_farmed"
                options={{
                  trend: false,
                  usdTrend: false,
                  endlessPerSecond: Number(data.tokens_per_second)
                }}
              />
            </>
          ) : (
            <>
              <KpiCard
                variant="usds"
                title="Total Staked"
                data={data}
                field="total_staked"
                options={{
                  currency: {
                    name: data.staking_token_symbol,
                    address: data.staking_token_address
                  }
                }}
              />
              <KpiCard
                title={`Total estimated ${data.rewards_token_symbol} rewards`}
                variant="sky"
                data={data}
                field="total_farmed"
                options={{
                  trend: false,
                  usdTrend: false,
                  endlessPerSecond: Number(data.tokens_per_second),
                  currency: {
                    name: data.rewards_token_symbol,
                    address: data.rewards_token_address
                  }
                }}
              />
            </>
          )}
        </Flex>

        <HistoricTabs address={address} />

        {farmMapping(data.address) !== 'chronicle_farm' && (
          <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
            <KpiCard
              title="APY"
              data={data}
              field="apy"
              options={{
                variants: { valuePercentageValue: 'dynamic' },
                percentage: true
              }}
            />

            {data.claimed !== undefined && (
              <>
                <KpiCard
                  title="Rewards Claimed"
                  data={data}
                  options={{
                    valueOptions: {
                      currencyValueOptions: {
                        currency: {
                          name: data.rewards_token_symbol,
                          address: data.rewards_token_address
                        }
                      }
                    }
                  }}
                  field="claimed"
                />
                <KpiCard
                  title="Percentage Rewards Claimed"
                  data={data}
                  field="claimed_percentage"
                  options={{
                    variants: { valuePercentageValue: 'dynamic' },
                    percentage: true
                  }}
                />
              </>
            )}
          </Flex>
        )}
        {farmMapping(data.address) === 'sky_farm' && (
          <>
            <Heading size="3">Boosted Rewards</Heading>
            <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
              <KpiCard
                title="Boosted Rewards Claimed"
                data={data}
                options={{
                  valueOptions: {
                    currencyValueOptions: {
                      currency: {
                        name: data.rewards_token_symbol,
                        address: data.rewards_token_address
                      }
                    }
                  }
                }}
                field="boosted_rewards_claimed"
              />
              <KpiCard
                title="Boosted Rewards Unclaimed"
                data={data}
                options={{
                  valueOptions: {
                    currencyValueOptions: {
                      currency: {
                        name: data.rewards_token_symbol,
                        address: data.rewards_token_address
                      },
                      trendVariant: trendVariantInverted
                    }
                  }
                }}
                field="boosted_rewards_unclaimed"
              />
              <KpiCard
                title="Boosted Rewards Claimed Percentage"
                data={data}
                field="boosted_rewards_claimed_percentage"
                options={{
                  variants: { valuePercentageValue: 'dynamic' },
                  percentage: true
                }}
              />
            </Flex>
          </>
        )}

        <ActivityCard url={`/farms/${address}/activity/`} linkTo="activity" />
        <Flex gap={['m', 's']} flexDirection={['column', 'row']}>
          <Flex gap={['m', 's']} flexDirection="column" width="100%">
            <LinkWrapper
              to="stakers"
              // @ts-ignore
              as={Link}
              variant="kpiCardLinkWithBlendMode">
              <KpiCard
                title="N° of Stakers"
                data={data}
                field="depositors"
                options={{
                  linkArrow: true,
                  variants: { valuePercentageValue: 'dynamic' }
                }}
              />
            </LinkWrapper>
            <TopDepositors address={address} flexBasis="100%" />
          </Flex>

          <Flex flexBasis="100%" flexDirection="column" alignItems="center">
            <Heading size="3">Stakers Distribution</Heading>
            <DepositorsDonutChart address={address} />
          </Flex>
        </Flex>
      </Page>
    );
  }

  return null;
}
